import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, UploadCloud } from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import { getInvoiceDetails, uploadShipmentDoc } from "../../../reducers/shipment.slice";
import { generatePDF } from "../../../utils/generateInvoice";
import { uploadPayoutInvoice } from "../../../reducers/payouts.slice";
import { Button } from "../../../components/ui/button";
import PageHeader from "../../../components/shared/PageHeader";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const uploadBoxBase = {
  minHeight: 200,
  border: "1px dashed rgba(255,255,255,0.45)",
  borderRadius: 8,
  marginBottom: 16,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  transition: "background 0.15s, border-color 0.15s",
};

const UploadShipmentReceiptPage = ({ dialogMode = false, shipmentId: shipmentIdProp, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const shipmentID = shipmentIdProp || location?.state?.shipmentId;
  const [showLoader, setShowLoader] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileUpload = (files) => {
    setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (showLoader) {
        event.preventDefault();
        event.returnValue = "API is still processing. Please wait.";
      }
    };

    const blockRefresh = (event) => {
      if (showLoader && (event.key === "F5" || (event.ctrlKey && event.key === "r"))) {
        event.preventDefault();
        showToast("API is still processing. Please wait.", "warning");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", blockRefresh);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", blockRefresh);
    };
  }, [showLoader]);

  const handleUploadShipmentDoc = () => {
    setShowLoader(true);
    const docFormData = new FormData();
    uploadedFiles.forEach((file) => {
      docFormData.append("files", file);
    });
    const payload = {
      itemId: shipmentID,
      docType: "RECEIPT",
    };
    dispatch(uploadShipmentDoc({ payload, docFormData })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (!res?.payload?.generate_invoice) {
        setShowLoader(false);
        if (dialogMode && onSuccess) onSuccess();
        else navigate(-1);
        return;
      }
      if (res?.payload) {
        dispatch(getInvoiceDetails({ id: shipmentID })).then((response) => {
          if (response?.error && Object.keys(response?.error)?.length > 0) {
            setShowLoader(false);
            showToast(response?.error?.message, "error");
            return;
          }
          const formdata = generatePDF(response?.payload);
          const uploadPayload = {
            id: res?.payload?.payout_id,
            document_type: "INVOICE",
            invoice_number: response?.payload?.invoice_number,
          };

          dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
            if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
              setShowLoader(false);
              showToast(uploadResponse?.error?.message, "error");
              return;
            }
            setShowLoader(false);
            showToast("Receipt uploaded successfully", "success");
            if (dialogMode && onSuccess) onSuccess();
            else navigate(-1);
          });
        });
      }
    });
  };

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: dialogMode ? 0 : isMobile ? "8px 0 32px" : "24px 0 48px" }}>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div style={{ width: "100%", maxWidth: 560 }}>
        {!dialogMode && (
          <>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: 18,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>

            <PageHeader title="Upload Shipment Receipt" subtitle={`Upload the shipment label for the shipment ID: ${shipmentID}`} />
          </>
        )}

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-input").click()}
            style={{
              ...uploadBoxBase,
              width: "100%",
              background: dragOver ? "rgba(0,173,181,0.08)" : "rgba(255,255,255,0.025)",
              borderColor: dragOver ? "#00ADB5" : "rgba(255,255,255,0.28)",
            }}
          >
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <UploadCloud size={42} color="#00ADB5" />
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Click here or Drop a file to upload label</span>
              <span style={{ color: "#8b9296", fontSize: 12 }}>(Accepted File Types: .jpg, .png, .pdf)</span>
            </span>
            <input id="file-input" type="file" accept=".jpg,.png,.pdf" style={{ display: "none" }} onChange={handleFileInputChange} multiple />
          </button>

          <div style={{ marginTop: 18 }}>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Uploaded Files</div>
            {uploadedFiles.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.025)",
                      padding: 12,
                      borderRadius: 8,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, color: "#c3c8cc", fontSize: 13 }}>
                      <FileText size={15} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveFile(index)} style={{ color: "#f27059", flexShrink: 0 }}>
                      <Trash2 size={14} /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#8b9296", fontSize: 13 }}>No files uploaded</div>
            )}
          </div>
        </div>

        <Button type="button" onClick={handleUploadShipmentDoc} disabled={uploadedFiles.length === 0} style={{ width: "100%", marginTop: 22 }}>
          Submit
        </Button>
        {dialogMode && (
          <Button type="button" variant="outline" onClick={onCancel} style={{ width: "100%", marginTop: 10 }}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadShipmentReceiptPage;
